import { useEffect, useRef, useState } from "react";

// Animates a number from 0 → target when the element scrolls into view.
// Accepts numeric `to`, optional `duration` (ms), `decimals`, prefix and suffix.
// Returns the current display value as a string.
export function useCountUp({ to, duration = 1400, decimals = 0, prefix = "", suffix = "" }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ref.current || startedRef.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const t0 = performance.now();
            const step = (now) => {
              const p = Math.min(1, (now - t0) / duration);
              const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
              setValue(eased * to);
              if (p < 1) requestAnimationFrame(step);
              else setValue(to);
            };
            requestAnimationFrame(step);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration]);

  const display = `${prefix}${value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;

  return { ref, display };
}
